// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

export default {
	/**
	 * to use methods initialize imported object (eg:- url) as url._parseUrl(window.location.toString()); /
	 * Currently, only handles two main cases:
	 *   - URLs without a protocol and hostname.
	 *   - URLs with http and https protocol and hostname.
	 *
	 * @param urlString the url String to parse
	 * */
	_parseUrl(urlStr: string) {
		let match;
		let rootPresent;
		let urlString = urlStr;
		this.protocol = '';
		this.host = '';
		this.pathname = '';
		this.search = '';
		this.hash = '';

		// Short-circuit null and empty string.
		if (!urlString) {
			return;
		}

		rootPresent = false;
		// it is assumed that the presence of a protocol implies the presence of a hostname
		// so the root is assumed to be present if we see a protocol in the url
		match = urlString.match(/^(http|https):\/\//);
		if (match && match.length >= 1) {
			rootPresent = true;
		}

		if (rootPresent) {
			// check if protocol is http/https.
			// set to "" if neither or absent
			// exec returns the first match with the group at index 1.
			match = (/^(http|https):\/\//).exec(urlString);
			if (match && match.length >= 2) {
				this.protocol = match[1];
				urlString = urlString.replace(match[0], '');
			}

			// check for the host name and port
			// Regex should match any character up to the first instance of '/', '?', or '#'.
			match = urlString.match(/^[^\/\?#]*/); // eslint-disable-line no-useless-escape
			if (match && match.length >= 1) {
				this.host = match[0];
				urlString = urlString.replace(this.host, '');
			}
		}

		// check for the path
		// Regex should match any character up to the first instance of '?' or '#'.
		match = urlString.match(/^[^\?#]*/); // eslint-disable-line no-useless-escape
		if (match && match.length >= 1) {
			this.pathname = match[0];
			urlString = urlString.replace(this.pathname, '');
		}

		// check for search params
		// Regex should match any character up to the first instance of '#'.
		match = urlString.match(/^[^#]*/);
		if (match && match.length >= 1) {
			this.search = match[0];
			urlString = urlString.replace(this.search, '');
		}

		// The rest of the string should be the url fragment only if it starts with '#'.
		// Otherwise, it's probably garbage and can be ignored.
		match = urlString.match(/^#.*$/);
		if (match && match.length >= 1) {
			this.hash = match[0];
		}
	},

	/**
	 * @param options Optional param - Currently supported options:
	 *   ignoreRoot: url should start at the path. eg. /plans?plan=team.
	 *               This is useful in scenarios where we want to do an app.navigate
	 *               Example input for "ignoreRoot":
	 *                 new url('/plans').addParam('plan', 'team').toString({ignoreRoot: true});
	 *               output for above input: /plans?plan=team
	 * @returns string form of the url
	 */
	toString(options: Object) {
		const ignoreRoot = options && options.ignoreRoot;
		// should only show the root if there is one present
		const hasRoot = (this.protocol && this.host);

		return ((hasRoot && !ignoreRoot) ? (`${this.protocol}://${this.host}`) : '') + this.pathname + this.search + this.hash;
	},

	/**
	 * Returns the decoded query parameter value with the provided name.
	 *
	 * @param name Required param - Name of param to be searched
	 * @param url Optional param - Custom URL in which to search given query parameter
	 * @returns param value from the url
	 */
	getParam(name: string, url: ?string) {
		const value = this._getParamRaw(name, url);
		if (value) {
			// Replace pluses with spaces and return the decoded version.
			return decodeURIComponent(value.replace(/\+/g, ' '));
		}
		return null;
	},

	/**
	 * Returns the raw query parameter value with the provided name.
	 *
	 * @param name Required param - Name of param to be searched
	 * @param url Optional param - Custom URL in which to search given query parameter
	 * @returns param value from the url
	 */
	_getParamRaw(name: string, url: ?string = this.search) {
		let href = url;
		if (href) {

		}
		else {
			href = document.location.href;
		}
		// matches the first query parameter. exec returns the first match only.
		const match = (new RegExp(`[?&]${name}=([^&#]*)`)).exec(href);
		// match[1] is the first group, which is the parameter value.
		return match && match[1];
	},

	/**
	 * Returns whether the search query has the named parameter in it.
	 *
	 * @param name Required param - Name of param to be searched
	 * @returns true if the search query contains the parameter with any value (incl. empty).
	 */
	hasParam(name: string) {
		// matches the first query parameter. exec returns the first match only.
		const match = (new RegExp(`[?&]${name}=([^&#]*)`)).exec(this.search);
		return (match && match.length >= 1);
	},

	/**
	 * Adds a query parameter to the url.
	 *
	 * @param param Required param - Name of param to be added
	 * @param value Required param - Value of param to be added
	 * @return this url object instance
	 */
	addParam(param: string, value: string/* , options*/) {
		let returnValue = this;
		if (this.search && this.hasParam(param)) {
			// if param already exists, use replace
			returnValue = this.replaceParam(param, value);
		}
		else {
			if (!this.search) {
				this.search = `?${param}=${encodeURIComponent(value)}`;
			}
			else {
				this.search += `&${param}=${encodeURIComponent(value)}`;
			}
		}

		return returnValue;
	},

	/**
	 * Replaces the value of the named query parameter.
	 *
	 * @param param Required param - Name of param to be replaced
	 * @param value Required param - New value of param to be replaced
	 * @param options Optional param - Currently supported options:
	 *    replaceOnly: function will strictly do a replace only, will not try to add if the param is absent
	 * @returns this url object instance
	 */
	replaceParam(param: string, value: string, options: Object) {
		const replaceOnly = options && options.replaceOnly;
		let match;

		if (this.search && this.hasParam(param)) {
			match = (new RegExp(`${param}=${this._getParamRaw(param)}`)).exec(this.search);
			if (match) {
				this.search = this.search.replace(match[0], `${param}=${encodeURIComponent(value)}`);
			}
		}
		else if (!replaceOnly) {
			// if param does not exist, use add if replaceOnly is false.
			return this.addParam(param, value);
		}

		return this;
	},

	/**
	 * Replaces the value of the named query parameter in the specified url.
	 *
	 * @param param Required param - Name of param to be replaced
	 * @param value Required param - New value of param to be replaced
	 * @param url Required URL in which replacement of given query parameter is to be done 
	 * @returns  url 
	 */
	replaceParamInUrl(param: string, value:string, url: ?string) {
		let match;
		// matches the first query parameter.
		match = this._getParamRaw(param,url);
		// if query param value is not same as expected replace it otherwise return the same url
		if (match !== value) {
			url = url.replace(`${param}=${match}`, `${param}=${value}`);
			return url;
		}
		return url;
	},


	/**
	 * Removes a query parameter from the provided URL.
	 *
	 * @param param Required param - Name of param to be removed
	 * @returns this url object instance
	 */
	removeParam(param: string) {
		const value = this._getParamRaw(param);
		const regex = new RegExp(`${param}=${value}&?`);
		const match = regex.exec(this.search);
		let lastIndex;
		// remove only if param already exists
		if (this.search && this.hasParam(param)) {
			this.search = match
				? this.search.replace(match[0], '')
				: this.search;

			// if param was the only search param, we'll be left with only a '?'
			if (this.search === '?') {
				this.search = '';
			}

			// if param was the last param, there will be a '&' at the end
			lastIndex = this.search.length - 1;
			if (this.search.length > 0 && this.search.charAt(lastIndex) === '&') {
				this.search = this.search.substring(0, lastIndex);
			}
		}

		return this;
	},

	/**
	 * Returns all the query parameters in the url
	 *
	 * @returns object mapping param to its value
	 */
	getAllParams() {
		const paramMap = {};
		let paramArr = [];
		const search = this.search.replace(/^\?/, '');

		(search.split('&')).forEach((paramPair) => {
			paramArr = paramPair.split('=');
			paramMap[paramArr[0]] = decodeURIComponent(paramArr[1]);
		});

		return paramMap;
	},

	/**
	 * Adds multiple params to the url from the provided map object
	 *
	 * @param paramMap Required param - Map object of params to be added
	 *   eg. {"plan": "team", "step": "2"}
	 * @returns this url object instance
	 */
	addAllParams(paramMap: Object) {
		if (paramMap) {
			Object.keys(paramMap).forEach((key) => {
				if (key) {
					this.addParam(key, paramMap[key]);
				}
			});
		}
		return this;
	},

	/**
	 * Removes multiple params from the url
	 *
	 * @param paramList Optional param - List of params to be removed
	 *    if paramList is not provided, remove all params from the url
	 * @returns this url object instance
	 */
	removeAllParams(paramList: string[]) {
		if (paramList) {
			paramList.forEach((param) => {
				this.removeParam(param);
			});
		}
		else {
			this.search = '';
		}

		return this;
	}
};